// ACTIONS: rename a model credential, or change a plan login's runtime reference
// (`PATCH model-credentials/{id}`). Never its key: the form has no field for one, and the API
// answers with the last four characters only. A new key is a new credential (the old revoked).
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { RowAction } from "../settings/row-actions";
import { type Credential, subscriptionKeys } from "./queries";

export function CredentialEditButton({
  api,
  co,
  credential: c,
  className,
}: {
  api: Api;
  co: string;
  credential: Credential;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <RowAction
        icon={PencilIcon}
        label={`Edit ${c.label}`}
        tip="Edit"
        {...(className ? { className } : {})}
        onClick={() => setOpen(true)}
      />
      {open ? <EditDialog api={api} co={co} credential={c} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function EditDialog({
  api,
  co,
  credential: c,
  onClose,
}: {
  api: Api;
  co: string;
  credential: Credential;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const ids = { label: useId(), ref: useId() };
  const cli = c.kind === "cli_subscription";
  const [label, setLabel] = useState(c.label);
  const [ref, setRef] = useState(c.runtime_ref ?? "");
  const save = useMutation({
    mutationFn: () =>
      api.client.modelCredentials.update({
        params: { co, id: c.id },
        body: {
          label: label.trim(),
          ...(cli ? { runtime_ref: ref.trim() || null } : {}),
        },
      }),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: subscriptionKeys.credentials(co) });
      toast.add({ title: `${saved.label} saved`, type: "success" });
      onClose();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (label.trim()) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Edit {c.label}</DialogTitle>
            <DialogDescription>
              {cli
                ? "The name and where your CLI runs. Nothing secret is stored for a plan login."
                : "The name only. To change the key, add a new one and revoke this one."}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.label}>Name</FieldLabel>
            <Input
              id={ids.label}
              value={label}
              maxLength={200}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
          {cli ? (
            <Field>
              <FieldLabel htmlFor={ids.ref}>Runtime reference</FieldLabel>
              <Input
                id={ids.ref}
                value={ref}
                maxLength={200}
                onChange={(e) => setRef(e.target.value)}
              />
              <FieldDescription>Optional, like the machine or session name.</FieldDescription>
            </Field>
          ) : null}
          {save.isError ? (
            <p role="alert" className="text-xs text-danger-text">
              {problemText(save.error, "Not saved.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!label.trim() || save.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
